import cognitoAuthService from './cognitoAuthService';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'nh_auth_token';
const USER_KEY = 'nh_user';
const isCognito = () => process.env.REACT_APP_AUTH_PROVIDER === 'cognito';

class AuthService {
  /**
   * Login and store token
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
   */
  async login(email, password) {
    try {
      let data;

      if (isCognito()) {
        // Cognito flow: authenticate with Cognito, then exchange token with backend
        const cognitoResult = await cognitoAuthService.signIn(email, password);

        const response = await fetch(`${API_BASE_URL}/auth/cognito-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: cognitoResult.idToken }),
        });

        data = await response.json();
      } else {
        // Local flow: direct email+password to backend
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        data = await response.json();
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Login failed' };
      }

      // If context selection is needed, return contexts without storing session token
      if (data.data.needsContextSelection) {
        localStorage.setItem('nh_context_token', data.data.contextToken);
        return {
          success: true,
          needsContextSelection: true,
          contexts: data.data.contexts,
          user: data.data.user
        };
      }

      // Full login — store token and user
      this.setToken(data.data.token);
      this.setUser(data.data.user);

      const { token, ...sessionContext } = data.data;
      return { success: true, user: sessionContext };
    } catch (error) {
      console.error('Login error:', error);
      // Handle Cognito-specific errors
      if (error.code === 'NEW_PASSWORD_REQUIRED') {
        return { success: false, error: 'New password required. Please set a new password.', newPasswordRequired: true, cognitoUser: error.cognitoUser };
      }
      // GH-11: surface PASSWORD_RESET_REQUIRED so Login.js switches to the
      // forgot-password UI. Previously this fell through to "Invalid email
      // or password" and blocked users for weeks.
      if (error.code === 'PASSWORD_RESET_REQUIRED' || error.code === 'PasswordResetRequiredException') {
        return {
          success: false,
          error: 'Password reset required. Check your email for a verification code, then set a new password.',
          passwordResetRequired: true,
          email,
        };
      }
      if (error.code === 'NotAuthorizedException') {
        return { success: false, error: 'Invalid email or password' };
      }
      if (error.code === 'UserNotFoundException') {
        return { success: false, error: 'Invalid email or password' };
      }
      return { success: false, error: error.message || 'An error occurred during login' };
    }
  }

  /**
   * Select login context (step 2 of two-step login)
   */
  async selectContext(practiceGroupId, role, practiceId) {
    try {
      const contextToken = localStorage.getItem('nh_context_token');
      if (!contextToken) {
        return { success: false, error: 'Context token expired. Please login again.' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/select-context`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${contextToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ practiceGroupId, role, practiceId }),
      });

      const data = await response.json();

      if (!data.success) {
        return { success: false, error: data.error || 'Context selection failed' };
      }

      localStorage.removeItem('nh_context_token');
      this.setToken(data.data.token);
      this.setUser(data.data.user);

      const { token, ...sessionContext } = data.data;
      return { success: true, user: sessionContext };
    } catch (error) {
      console.error('Select context error:', error);
      return { success: false, error: 'An error occurred during context selection' };
    }
  }

  /**
   * Switch to a different practice within the same group
   */
  async switchPractice(practiceId) {
    try {
      const token = this.getToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/switch-practice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ practiceId }),
      });

      const data = await response.json();

      if (!data.success) {
        return { success: false, error: data.error || 'Practice switch failed' };
      }

      this.setToken(data.data.token);

      const { token: newToken, ...sessionContext } = data.data;
      return { success: true, user: sessionContext };
    } catch (error) {
      console.error('Switch practice error:', error);
      return { success: false, error: 'An error occurred while switching practice' };
    }
  }

  /**
   * Logout and clear stored data
   */
  logout() {
    if (isCognito()) {
      cognitoAuthService.signOut();
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('selectedPracticeId');
    localStorage.removeItem('nh_context_token');
  }

  /**
   * Get stored token
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Set token in storage
   * @param {string} token
   */
  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Get stored user
   * @returns {Object|null}
   */
  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Set user in storage
   * @param {Object} user
   */
  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired (basic check)
    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) return false;
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Decode JWT token (without verification)
   * @param {string} token
   * @returns {Object|null}
   */
  decodeToken(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch {
      return null;
    }
  }

  /**
   * Get current user from token
   * @returns {Object|null}
   */
  getCurrentUser() {
    const token = this.getToken();
    if (!token) return null;
    return this.decodeToken(token);
  }

  /**
   * Refresh token
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async refreshToken() {
    try {
      const token = this.getToken();
      if (!token) {
        return { success: false, error: 'No token to refresh' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!data.success) {
        return { success: false, error: data.error || 'Token refresh failed' };
      }

      this.setToken(data.data.token);
      return { success: true };
    } catch (error) {
      console.error('Token refresh error:', error);
      return { success: false, error: 'An error occurred during token refresh' };
    }
  }

  /**
   * Validate current token with server
   * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
   */
  async validateToken() {
    try {
      const token = this.getToken();
      if (!token) {
        return { success: false, error: 'No token' };
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Only the server saying "this token is not valid" justifies destroying the
      // session. A 5xx, a gateway timeout page, or a body that is not JSON is the
      // server or the network having a bad moment, not the token being wrong.
      if (response.status === 401 || response.status === 403) {
        this.logout();
        let error = 'Token validation failed';
        try { error = (await response.json()).error || error; } catch { /* keep default */ }
        return { success: false, invalid: true, error };
      }

      let data;
      try {
        data = await response.json();
      } catch {
        return { success: false, transient: true, error: `Non-JSON response (HTTP ${response.status}) during validation` };
      }

      if (!response.ok || !data || data.success === false) {
        if (response.ok && data && data.success === false) {
          // 200 with an explicit failure is the server rejecting the token.
          this.logout();
          return { success: false, invalid: true, error: data.error || 'Token validation failed' };
        }
        return { success: false, transient: true, error: data?.error || `HTTP ${response.status} during validation` };
      }

      return { success: true, user: data.data };
    } catch (error) {
      // fetch() throws on network failure, on a dropped connection, and when the
      // browser aborts the request because the user navigated away mid-check.
      // None of those say anything about the token. This used to fall through to a
      // logout in AuthContext, so a quick navigation or a flaky connection threw a
      // logged-in user out and deleted their session. Found 2026-09-11 when an e2e
      // page-load test navigated while the auth check was in flight and landed on
      // /login with the token gone.
      console.error('Token validation error (transient, session kept):', error);
      return { success: false, transient: true, error: 'An error occurred during validation' };
    }
  }

  /**
   * Change password
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async changePassword(currentPassword, newPassword) {
    try {
      // Change in Cognito first if applicable
      if (isCognito()) {
        await cognitoAuthService.changePassword(currentPassword, newPassword);
      }

      // Also update on backend (handles local hash and audit logging)
      const token = this.getToken();
      const response = await fetch(`${API_BASE_URL}/auth/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!data.success) {
        return { success: false, error: data.error || 'Password change failed' };
      }

      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      return { success: false, error: error.message || 'An error occurred while changing password' };
    }
  }

  /**
   * Check setup status
   * @returns {Promise<{setupRequired: boolean, error?: string}>}
   */
  async checkSetupStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/setup/status`);
      const data = await response.json();

      if (!data.success) {
        return { setupRequired: false, error: data.error };
      }

      return { setupRequired: data.data.setupRequired };
    } catch (error) {
      console.error('Setup status check error:', error);
      return { setupRequired: false, error: 'Failed to check setup status' };
    }
  }

  /**
   * Complete initial setup
   * @param {Object} setupData
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async completeSetup(setupData) {
    try {
      const response = await fetch(`${API_BASE_URL}/setup/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(setupData),
      });

      const data = await response.json();

      if (!data.success) {
        return { success: false, error: data.error, errors: data.errors };
      }

      return { success: true, user: data.data.user };
    } catch (error) {
      console.error('Setup complete error:', error);
      return { success: false, error: 'An error occurred during setup' };
    }
  }

  /**
   * Get password requirements
   * @returns {Promise<{requirements: string[], error?: string}>}
   */
  async getPasswordRequirements() {
    try {
      const response = await fetch(`${API_BASE_URL}/setup/password-requirements`);
      const data = await response.json();

      if (!data.success) {
        return { requirements: [], error: data.error };
      }

      return data.data;
    } catch (error) {
      console.error('Get password requirements error:', error);
      return { requirements: [], error: 'Failed to get password requirements' };
    }
  }
}

const authService = new AuthService();
export default authService;
